/**
 * Палитра виджета из настроек организации.
 *
 * Material You (однотонная система): из одного `primary_color` вычисляем всю палитру.
 * Продуктовое решение и эволюция (HCT / MD утилиты): docs/adr/0016-embed-material-you-single-primary-color.md
 * Хью источника задаёт «характер» бренда; насыщенность нейтралей — едва заметный тинт.
 * Контраст и читаемость гарантированы на любом входном цвете.
 *
 * Обе темы: полная палитра из source color.
 */

export type EmbedThemeMode = 'light' | 'dark' | 'system';

// ---------------------------------------------------------------------------
// Утилиты цвета
// ---------------------------------------------------------------------------

function clamp(v: number, lo = 0, hi = 1): number {
  return Math.max(lo, Math.min(hi, v));
}

export function normalizeHexColor(value: unknown, fallback: string): string {
  const fb = String(fallback || '').trim() || '#000000';
  const raw = String(value ?? '').trim();
  if (!raw) return fb;
  if (/^#[0-9a-f]{3}$/i.test(raw)) {
    const h = raw.slice(1);
    return '#' + h.split('').map((c) => c + c).join('').toLowerCase();
  }
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw.toLowerCase();
  return fb;
}

function hexToRgb(hex: string): [r: number, g: number, b: number] {
  const n = normalizeHexColor(hex, '#000000').slice(1);
  return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((v) => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, '0')).join('');
}

/** Конвертация RGB [0-255] → HSL [0-360, 0-100, 0-100]. */
function rgbToHsl(r: number, g: number, b: number): [h: number, s: number, l: number] {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  const l = (max + min) / 2;

  if (d === 0) return [0, 0, l * 100];

  const s = d / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (max === rn) h = ((gn - bn) / d + 6) % 6;
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;

  return [h * 60, s * 100, l * 100];
}

/** HSL [0-360, 0-100, 0-100] → hex. */
function hslToHex(h: number, s: number, l: number): string {
  const sn = s / 100, ln = l / 100;
  const a = sn * Math.min(ln, 1 - ln);
  function f(n: number): number {
    const k = (n + h / 30) % 12;
    return ln - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  }
  return rgbToHex(f(0) * 255, f(8) * 255, f(4) * 255);
}

function linearize(c: number): number {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  return 0.2126 * linearize(rgb[0]) + 0.7152 * linearize(rgb[1]) + 0.0722 * linearize(rgb[2]);
}

function getContrastRatio(foreground: string, background: string): number {
  const a = getRelativeLuminance(foreground);
  const b = getRelativeLuminance(background);
  const hi = Math.max(a, b);
  const lo = Math.min(a, b);
  return (hi + 0.05) / (lo + 0.05);
}

/** Минимальный контраст обычного текста по WCAG AA. */
const MIN_TEXT_CONTRAST = 4.5;
/** Минимальный контраст focus indicator по WCAG AA. */
const MIN_FOCUS_CONTRAST = 3;
const ACCENT_FG_LIGHT = '#f8fafc';
const ACCENT_FG_DARK = '#0d1526';

type AccentPair = {
  bg: string;
  fg: string;
  tone: number;
  ratio: number;
};

/**
 * Пара «фон accent + текст на нём» для заданного тона.
 * Foreground выбирается по максимуму контраста, а не по порогу luminance:
 * на средних тонах (жёлтый, циан) порог ошибался и оставлял белый текст.
 */
function accentPairAtTone(h: number, s: number, tone: number): AccentPair {
  const bg = hslToHex(h, s, tone);
  const lightRatio = getContrastRatio(ACCENT_FG_LIGHT, bg);
  const darkRatio = getContrastRatio(ACCENT_FG_DARK, bg);
  const fg = lightRatio >= darkRatio ? ACCENT_FG_LIGHT : ACCENT_FG_DARK;
  return { bg, fg, tone, ratio: Math.max(lightRatio, darkRatio) };
}

/**
 * Ищет ближайший к `targetTone` тон в направлении `direction`,
 * где лучший foreground даёт не меньше `minRatio`.
 */
function pickAccentPair(
  h: number,
  s: number,
  targetTone: number,
  direction: 1 | -1,
  minRatio = MIN_TEXT_CONTRAST
): AccentPair {
  for (let tone = targetTone; tone >= 0 && tone <= 100; tone += direction) {
    const pair = accentPairAtTone(h, s, tone);
    if (pair.ratio >= minRatio) return pair;
  }

  let best = accentPairAtTone(h, s, targetTone);
  for (let tone = 0; tone <= 100; tone += 1) {
    const pair = accentPairAtTone(h, s, tone);
    if (pair.ratio > best.ratio) best = pair;
  }
  return best;
}

/**
 * Hover-фон двигаем в сторону роста контраста с уже выбранным foreground:
 * светлый текст — темнее фон, тёмный текст — светлее фон.
 */
function pickAccentHover(h: number, s: number, base: AccentPair): string {
  const direction = base.fg === ACCENT_FG_DARK ? 1 : -1;
  for (let offset = 6; offset <= 40; offset += 1) {
    const tone = base.tone + direction * offset;
    if (tone < 0 || tone > 100) break;
    const bg = hslToHex(h, s, tone);
    if (getContrastRatio(base.fg, bg) >= MIN_TEXT_CONTRAST) return bg;
  }
  return base.bg;
}

/**
 * Ищет ближайший к `targetTone` тон в направлении `direction`, где цвет
 * даёт не меньше `minRatio` против всех `backgrounds`.
 * Используется для focus-ring (3:1) и link accent (4.5:1 на bg и card).
 */
function pickToneForContrast(
  h: number,
  s: number,
  targetTone: number,
  direction: 1 | -1,
  backgrounds: readonly string[],
  minRatio: number
): string {
  for (let tone = targetTone; tone >= 0 && tone <= 100; tone += direction) {
    const hex = hslToHex(h, s, tone);
    if (backgrounds.every((bg) => getContrastRatio(hex, bg) >= minRatio)) return hex;
  }

  let best = hslToHex(h, s, targetTone);
  let bestScore = -1;
  for (let tone = 0; tone <= 100; tone += 1) {
    const hex = hslToHex(h, s, tone);
    const score = Math.min(...backgrounds.map((bg) => getContrastRatio(hex, bg)));
    if (score > bestScore) {
      bestScore = score;
      best = hex;
    }
  }
  return best;
}

// Legacy export used by SessionPage / ServicesListPage etc.
export function mixHexColors(base: string, target: string, weight: number): string {
  const [ar, ag, ab] = hexToRgb(base);
  const [br, bg, bb] = hexToRgb(target);
  const t = clamp(weight);
  return rgbToHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

// ---------------------------------------------------------------------------
// Material You — однотонная палитра из одного source color
// ---------------------------------------------------------------------------

/**
 * Строит все CSS-переменные темы из одного hex.
 *
 * Алгоритм:
 * 1. Извлекаем H и S из source color.
 * 2. S нормализуем в [45, 100] — слишком серые акценты подтягиваем до 45%.
 * 3. `--accent` (ссылки/иконки): ближайший к 38 (light) / 72 (dark) тон,
 *    где цвет даёт ≥ 4.5:1 против `--bg` и `--bg-elevated`.
 * 4. `--accent-bg` (шапки, primary buttons): ближайший к 38/58 тон, где
 *    максимум из светлого/тёмного foreground даёт ≥ 4.5:1.
 * 5. Neutral saturation = S * 0.06 (≤ 6%) — чуть заметный хью-тинт в фонах/бордерах.
 * 6. Lightness нейтралей фиксирована — гарантируем читаемость при любом H.
 */
export function buildMaterialTheme(
  sourceHex: string,
  colorScheme: 'light' | 'dark'
): Record<string, string> {
  const [r, g, b] = hexToRgb(normalizeHexColor(sourceHex, '#1d4ed8'));
  const [h, rawS] = rgbToHsl(r, g, b);

  // Нормализуем насыщенность акцента
  const s = clamp(rawS, 45, 100);
  // Нейтральная насыщенность — тонкий хью-тинт для фонов
  const ns = clamp(s * 0.06, 0, 6);

  const accentL = 38;
  const accentDarkL = 72;

  if (colorScheme === 'dark') {
    const bg = hslToHex(h, ns, 6);
    const bgElevated = hslToHex(h, ns * 0.5, 9);
    const accent = pickToneForContrast(h, s, accentDarkL, 1, [bg, bgElevated], MIN_TEXT_CONTRAST);
    const accentPair = pickAccentPair(h, s, 58, 1);
    const accentBgHover = pickAccentHover(h, s, accentPair);
    return {
      '--accent':                 accent,
      '--accent-bg':              accentPair.bg,
      '--accent-bg-hover':        accentBgHover,
      '--accent-fg':              accentPair.fg,
      '--btn-primary-bg':         accentPair.bg,
      '--btn-primary-bg-hover':   accentBgHover,
      '--btn-primary-fg':         accentPair.fg,
      '--focus-ring':             pickToneForContrast(h, s, 75, 1, [bg, bgElevated], MIN_FOCUS_CONTRAST),
      '--bg':                     bg,
      '--bg-elevated':            bgElevated,
      '--bg-soft':                hslToHex(h, ns * 1.4,  14),
      '--text':                   hslToHex(h, ns * 2.5,  92),
      '--text-muted':             hslToHex(h, ns * 2.8,  60),
      '--border':                 hslToHex(h, ns * 1.6,  22),
      '--btn-secondary-bg':       hslToHex(h, ns * 1.2,  16),
      '--btn-secondary-bg-hover': hslToHex(h, ns * 1.6,  22),
      '--btn-secondary-fg':       hslToHex(h, ns * 2.5,  88),
      '--btn-secondary-border':   hslToHex(h, ns * 1.4,  28),
    };
  }

  // Светлая тема: полная тональная палитра
  const bg = hslToHex(h, ns, 96);
  const bgElevated = hslToHex(h, ns * 0.5, 99);
  const accent = pickToneForContrast(h, s, accentL, -1, [bg, bgElevated], MIN_TEXT_CONTRAST);
  const accentPair = pickAccentPair(h, s, 38, -1);
  const accentBgHover = pickAccentHover(h, s, accentPair);

  return {
    '--accent':                 accent,
    '--accent-bg':              accentPair.bg,
    '--accent-bg-hover':        accentBgHover,
    '--accent-fg':              accentPair.fg,
    '--btn-primary-bg':         accentPair.bg,
    '--btn-primary-bg-hover':   accentBgHover,
    '--btn-primary-fg':         accentPair.fg,
    '--focus-ring':             pickToneForContrast(h, s, 70, -1, [bg, bgElevated], MIN_FOCUS_CONTRAST),
    '--bg':                     bg,
    '--bg-elevated':            bgElevated,
    '--bg-soft':                hslToHex(h, ns * 1.4,  92),
    '--text':                   hslToHex(h, ns * 2.5,  10),
    '--text-muted':             hslToHex(h, ns * 2.8,  42),
    '--border':                 hslToHex(h, ns * 1.6,  82),
    '--btn-secondary-bg':       hslToHex(h, ns * 1.2,  93),
    '--btn-secondary-bg-hover': hslToHex(h, ns * 1.6,  87),
    '--btn-secondary-fg':       hslToHex(h, ns * 2.5,  12),
    '--btn-secondary-border':   hslToHex(h, ns * 1.4,  80),
  };
}

/** Имена CSS-переменных, которые выставляет `applyEmbedWidgetTheme` (для сброса). */
export const EMBED_WIDGET_THEME_VAR_NAMES = [
  '--accent',
  '--accent-bg',
  '--accent-bg-hover',
  '--accent-fg',
  '--btn-primary-bg',
  '--btn-primary-bg-hover',
  '--btn-primary-fg',
  '--bg',
  '--bg-elevated',
  '--bg-soft',
  '--text',
  '--text-muted',
  '--border',
  '--focus-ring',
  '--btn-secondary-bg',
  '--btn-secondary-bg-hover',
  '--btn-secondary-fg',
  '--btn-secondary-border'
] as const;

export function parseEmbedThemeMode(raw: unknown): EmbedThemeMode {
  const s = String(raw ?? '').trim().toLowerCase();
  if (s === 'light' || s === 'dark' || s === 'system') return s;
  return 'system';
}

/** Режим из настроек + факт тёмной ОС (для `system`). */
export function resolveEmbedIsDark(mode: EmbedThemeMode, prefersDark: boolean): boolean {
  if (mode === 'dark') return true;
  if (mode === 'light') return false;
  return prefersDark;
}

/**
 * Применяет Material You палитру из primary_color виджета.
 * Читает только поле `primary_color` — остальные цветовые поля больше не используются.
 */
export function applyEmbedWidgetTheme(
  root: HTMLElement,
  widget: Record<string, unknown> | undefined | null,
  colorScheme: 'light' | 'dark'
): void {
  if (!widget || typeof widget !== 'object') return;
  const sourceHex = normalizeHexColor(widget.primary_color, '#1d4ed8');
  const vars = buildMaterialTheme(sourceHex, colorScheme);
  for (const [name, value] of Object.entries(vars)) {
    if (value) root.style.setProperty(name, value);
  }
}

export function clearEmbedWidgetTheme(root: HTMLElement): void {
  for (const name of EMBED_WIDGET_THEME_VAR_NAMES) {
    root.style.removeProperty(name);
  }
}

// ---------------------------------------------------------------------------
// Legacy exports (используются в SessionPage и других страницах)
// ---------------------------------------------------------------------------

/** @deprecated используй buildMaterialTheme — оставлен для обратной совместимости */
export function resolveWidgetTheme(widgetSettings: Record<string, unknown> | null | undefined): { primary_color: string } {
  const source = widgetSettings ?? {};
  return { primary_color: normalizeHexColor(source.primary_color, '#1d4ed8') };
}
